import { api } from '~/data/api'
import { IInspectionChecklist, ILeanInspectionChecklist } from '~/utils/types'

export interface IUpdateInspectionChecklistProps {
  inspection_checklist_id?: string
  inspection_checklist: IInspectionChecklist
}

export async function updateInspectionChecklist({
  inspection_checklist_id,
  inspection_checklist,
}: IUpdateInspectionChecklistProps) {
  const checklist_photo = inspection_checklist.photo
  delete inspection_checklist.photo_url

  if (checklist_photo && inspection_checklist_id) {
    const photoUploaded = await uploadInspectionChecklistImage({
      inspectionChecklistId: inspection_checklist_id,
      photo: checklist_photo,
    })
    if (photoUploaded.status === 200) {
      inspection_checklist.photo_key = photoUploaded.data.photo_key
    }
  } else if (checklist_photo === null && inspection_checklist_id) {
    const deleted = await deleteInspectionChecklistImage({
      inspectionChecklistId: inspection_checklist_id,
    })
    if (Object.keys(inspection_checklist).length === 1) {
      // if the only changed attribute is the image
      return deleted.data
    }
  }
  delete inspection_checklist.photo
  if (Object.keys(inspection_checklist).length > 0) {
    const response = await api().put(
      `/inspection_checklist/${inspection_checklist_id}`,
      inspection_checklist,
    )
    return response.data
  }
}

export async function createInspectionChecklist(inspection_checklist: IInspectionChecklist) {
  const checklist_photo = inspection_checklist.photo
  delete inspection_checklist.photo
  delete inspection_checklist.photo_url
  const response = await api().post(`/inspection_checklist`, inspection_checklist)
  if (response.status === 201 && checklist_photo) {
    // upload photo
    await uploadInspectionChecklistImage({
      inspectionChecklistId: response.data._id,
      photo: checklist_photo,
    })
  }
  return response.data
}

export async function deleteInspectionChecklist(inspection_checklist_id: string) {
  const response = await api().delete(`/inspection_checklist/${inspection_checklist_id}`)
  return response.data
}

export async function exportInspectionChecklist(inspection_checklist_id: string) {
  const response = await api().get(`/inspection_checklist/${inspection_checklist_id}/export`, {
    responseType: 'blob',
  })
  return response
}

export async function getComponentsToAircraftModel(aircraft_model_id: string) {
  const response = await api().get(`/component?aircraft_model_id=${aircraft_model_id}`)
  return response.data
}

export async function listInspectionChecklists(
  lean = true,
): Promise<ILeanInspectionChecklist[] | IInspectionChecklist[]> {
  const response = await api().get(`/inspection_checklist`, {
    params: {
      lean,
    },
  })
  return response.data
}

export interface IGetInspectionChecklistById {
  inspection_checklist_id: string
  lean?: boolean
}

export async function getInspectionChecklistById({
  inspection_checklist_id,
  lean = true,
}: IGetInspectionChecklistById): Promise<IInspectionChecklist> {
  const response = await api().get(`/inspection_checklist/${inspection_checklist_id}`, {
    params: {
      lean,
    },
  })

  return response.data
}

export interface IGetInspectionChecklistPhotoCategoryImage {
  inspection_checklist_id: string
  category_uuid: string
}
export async function getInspectionChecklistPhotoCategoryImage({
  inspection_checklist_id,
  category_uuid,
}: IGetInspectionChecklistPhotoCategoryImage): Promise<string> {
  const response = await api().get(
    `/inspection_checklist/${inspection_checklist_id}/get_photo_category_image/${category_uuid}`,
  )

  return response.data
}

export interface IUpdateInspectionChecklistPhotoCategoryImage {
  inspection_checklist_id: string
  category_uuid: string
  image: File | false
}
export async function updateInspectionChecklistPhotoCategoryImage({
  inspection_checklist_id,
  category_uuid,
  image,
}: IUpdateInspectionChecklistPhotoCategoryImage): Promise<string> {
  const formData = new FormData()
  formData.append('category_uuid', category_uuid)
  formData.append('image', image || new File([], ''))

  const response = await api().patch(
    `/inspection_checklist/${inspection_checklist_id}/update_photo_category_image`,
    formData,
  )

  return response.data
}

export interface IUploadInspectionChecklistImage {
  inspectionChecklistId: string
  photo: File
}
export async function uploadInspectionChecklistImage({
  inspectionChecklistId,
  photo,
}: IUploadInspectionChecklistImage) {
  const formData = new FormData()
  formData.append('inspection_checklist_photo', photo)

  return await api().put(`/inspection_checklist/${inspectionChecklistId}/import_photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export interface IGetInspectionChecklistImage {
  photo_url: string
}
export const getInspectionChecklistImage = async (
  inspectionChecklistId?: string,
): Promise<IGetInspectionChecklistImage | null> => {
  if (!inspectionChecklistId) return null
  const response = await api().get(`/inspection_checklist/${inspectionChecklistId}/find_photo`)
  return response.data
}

export interface IDeleteInspectionChecklistImage {
  inspectionChecklistId: string
}
export async function deleteInspectionChecklistImage({
  inspectionChecklistId,
}: IDeleteInspectionChecklistImage) {
  return await api().delete(`/inspection_checklist/${inspectionChecklistId}/delete_photo`)
}
